import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

interface Package {
  id: number;
  name: string;
  description: string;
  price: number;
  includes: string;
}

interface Player {
  id: number;
  name: string;
  email: string;
  city: string;
  country: string;
  bowlerId: number;
  bowlerName: string;
  average: number;
  handicap: number;
  registrationStatus?: string;
}

const CheckoutComponent: React.FC = () => {
  const { playerId, leagueId } = useParams<{ playerId: string; leagueId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [player, setPlayer] = useState<Player | null>(null);
  const [leagueName, setLeagueName] = useState<string>('');
  const [availablePackages, setAvailablePackages] = useState<Package[]>([]);
  const [selectedPackages, setSelectedPackages] = useState<Package[]>([]);
  const [paidPackages, setPaidPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [showPaymentSection, setShowPaymentSection] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptData, setReceiptData] = useState<{
    amount: number;
    paymentMethod: string;
    packages: Package[];
    registrationStatus: string;
    timestamp: string;
  } | null>(null);

  // Calculate total
  const selectedTotal = selectedPackages.reduce((sum, pkg) => sum + parseFloat(pkg.price.toString()), 0);
  const selectedPackageIds = selectedPackages.map(pkg => pkg.id);

  const fetchPlayerDetails = useCallback(async () => {
    if (!playerId || !leagueId) return;

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      
      // Fetch player details
      const response = await fetch(`${apiUrl}/checkin/player/${playerId}/league/${leagueId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPlayer(data.player);
        setAvailablePackages(data.availablePackages || []);
        setSelectedPackages(data.selectedPackages || []);
        setPaidPackages(data.paidPackages || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('checkout.errors.loadDetailsFailed'));
      }

      // Fetch league details
      const leagueResponse = await fetch(`${apiUrl}/leagues/${leagueId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (leagueResponse.ok) {
        const leagueData = await leagueResponse.json();
        setLeagueName(leagueData.name || 'Unknown League');
      }
    } catch (error) {
      console.error('Fetch details error:', error);
      setError(t('checkout.errors.loadDetailsFailed'));
    } finally {
      setLoading(false);
    }
  }, [playerId, leagueId, t]);

  useEffect(() => {
    fetchPlayerDetails();
  }, [fetchPlayerDetails]);

  const handlePackageToggle = (pkg: Package) => {
    setSelectedPackages(current => {
      const isSelected = current.find(p => p.id === pkg.id);
      if (isSelected) {
        return current.filter(p => p.id !== pkg.id);
      } else {
        return [...current, pkg];
      }
    });
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    setError('');

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/checkin/player/${playerId}/league/${leagueId}/save-changes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          selectedPackageIds
        })
      });

      if (response.ok) {
        setShowPaymentSection(true);
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('checkout.errors.saveFailed'));
      }
    } catch (error) {
      console.error('Save changes error:', error);
      setError(t('checkout.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCollectPayment = async () => {
    setSaving(true);
    setError('');

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/checkin/player/${playerId}/league/${leagueId}/collect-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          amount: selectedTotal,
          paymentMethod,
          packageIds: selectedPackageIds
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Set receipt data and show modal
        setReceiptData({
          amount: selectedTotal,
          paymentMethod,
          packages: [...selectedPackages],
          registrationStatus: data.registrationStatus,
          timestamp: new Date().toLocaleString()
        });
        setShowReceiptModal(true);
        
        // Reset form state and refresh player data
        setSelectedPackages([]);
        setShowPaymentSection(false);
        
        // Refresh player data to update available/paid packages
        await fetchPlayerDetails();
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('checkout.errors.paymentFailed'));
      }
    } catch (error) {
      console.error('Collect payment error:', error);
      setError(t('checkout.errors.paymentFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(`/admin/leagues/${leagueId}/manage`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-xl">{t('checkout.loadingDetails')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold mb-2">Error</h2>
          <p className="text-blue-200 mb-4">{error}</p>
          <button
            onClick={handleBack}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition duration-200"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBack}
                className="text-white hover:text-blue-300 transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-3xl font-bold text-white">{t('checkout.title')}</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {player && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Player Info Card */}
            <div className="lg:col-span-1">
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 h-fit sticky top-8">
                <div 
                  className="text-center mb-6 cursor-pointer hover:bg-white/5 rounded-lg p-3 transition duration-200"
                  onClick={() => navigate(`/admin/bowlers/${player.bowlerId}`)}
                >
                  <div className="text-4xl mb-3">🎳</div>
                  <h2 className="text-xl font-bold text-white mb-2">{player.bowlerName}</h2>
                  <div className="text-blue-200 space-y-1 text-sm">
                    <p><span className="font-semibold">{t('checkout.id')}:</span> {player.bowlerId}</p>
                    <p><span className="font-semibold">Status:</span> 
                      <span className={`ml-1 px-2 py-1 rounded text-xs font-medium ${
                        player.registrationStatus === 'completed' ? 'bg-green-500/20 text-green-300' :
                        player.registrationStatus === 'active' ? 'bg-blue-500/20 text-blue-300' :
                        player.registrationStatus === 'pending' ? 'bg-yellow-500/20 text-yellow-300' :
                        'bg-gray-500/20 text-gray-300'
                      }`}>
                        {player.registrationStatus || 'pending'}
                      </span>
                    </p>
                    <p><span className="font-semibold">{t('checkout.email')}:</span> {player.email}</p>
                    <p><span className="font-semibold">{t('checkout.location')}:</span> {player.city}, {player.country}</p>
                    <p><span className="font-semibold">{t('checkout.average')}:</span> {player.average}</p>
                    <p><span className="font-semibold">{t('checkout.handicap')}:</span> {player.handicap}</p>
                  </div>
                </div>

                {/* Order Summary */}
                <div className="border-t border-white/20 pt-4">
                  <h3 className="font-semibold text-white mb-3">{t('checkout.total')}</h3>
                  <div className="space-y-2 text-sm">
                    {selectedPackages.map((pkg) => (
                      <div key={pkg.id} className="flex justify-between text-blue-200">
                        <span className="truncate mr-2">{pkg.name}</span>
                        <span className="font-semibold">${parseFloat(pkg.price.toString()).toFixed(2)}</span>
                      </div>
                    ))}
                    {selectedPackages.length === 0 && (
                      <p className="text-blue-300 italic">{t('checkout.errors.selectPackage')}</p>
                    )}
                  </div>
                  
                  <div className="border-t border-white/20 mt-3 pt-3">
                    <div className="flex justify-between text-white font-bold text-lg">
                      <span>{t('checkout.total')}:</span>
                      <span>${selectedTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-6 space-y-3">
                  {selectedPackages.length > 0 && !showPaymentSection && (
                    <button
                      onClick={handleSaveChanges}
                      disabled={saving}
                      className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold rounded-lg transition duration-200"
                    >
                      {saving ? t('checkout.saving') : t('checkout.saveSelection')}
                    </button>
                  )}

                  {showPaymentSection && selectedPackages.length > 0 && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-white mb-2">{t('checkout.paymentMethod')}</label>
                        <select
                          value={paymentMethod}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer backdrop-blur-sm"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                            backgroundPosition: 'right 0.5rem center',
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: '1.5em 1.5em'
                          }}
                        >
                          <option value="cash" className="bg-gray-800 text-white">{t('checkout.cash')}</option>
                          <option value="card" className="bg-gray-800 text-white">{t('checkout.card')}</option>
                          <option value="check" className="bg-gray-800 text-white">{t('checkout.check')}</option>
                          <option value="transfer" className="bg-gray-800 text-white">{t('checkout.other')}</option>
                        </select>
                      </div>
                      
                      <button
                        onClick={handleCollectPayment}
                        disabled={saving}
                        className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded-lg transition duration-200"
                      >
                        {saving ? t('checkout.processing') : `${t('checkout.completePayment')} ($${selectedTotal.toFixed(2)})`}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Packages Bought */}
            <div className="lg:col-span-1">
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 h-fit">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                  <span className="mr-2">✅</span>
                  {t('checkout.packagesBought')}
                </h3>
                
                {paidPackages.length > 0 ? (
                  <div className="space-y-3">
                    {paidPackages.map((pkg) => (
                      <div key={pkg.id} className="bg-green-500/20 rounded-lg p-3 border border-green-500/30">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="text-white font-semibold text-sm">{pkg.name}</h4>
                            <p className="text-green-200 text-xs mt-1">{pkg.description}</p>
                          </div>
                          <div className="ml-2 text-right">
                            <p className="text-white font-bold text-sm">${parseFloat(pkg.price.toString()).toFixed(2)}</p>
                            <p className="text-green-400 text-xs">✓ Paid</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="border-t border-green-500/30 pt-3">
                      <div className="flex justify-between text-green-200 font-semibold">
                        <span>Total Paid:</span>
                        <span>${paidPackages.reduce((sum, pkg) => sum + parseFloat(pkg.price.toString()), 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <div className="text-3xl mb-2">📦</div>
                    <p className="text-blue-300 text-sm">{t('checkout.noPackages')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Package Selection */}
            <div className="lg:col-span-2">
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                <h3 className="text-xl font-bold text-white mb-6">{t('checkout.availablePackages')}</h3>
                
                {availablePackages.length > 0 ? (
                  <div className="grid gap-4">
                    {availablePackages.map((pkg) => {
                      const isSelected = selectedPackages.find(p => p.id === pkg.id);
                      const isPaid = paidPackages.find(p => p.id === pkg.id);
                      
                      // Don't show packages that are already paid for
                      if (isPaid) return null;
                      
                      return (
                        <div
                          key={pkg.id}
                          className={`relative rounded-lg p-4 border cursor-pointer transition duration-200 ${
                            isSelected
                              ? 'bg-blue-500/20 border-blue-500'
                              : 'bg-white/5 border-white/20 hover:bg-white/10'
                          }`}
                          onClick={() => handlePackageToggle(pkg)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3">
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                  isSelected
                                    ? 'bg-blue-500 border-blue-500'
                                    : 'border-white/40'
                                }`}>
                                  {isSelected && (
                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </div>
                                <div>
                                  <h4 className="text-white font-semibold text-lg">{pkg.name}</h4>
                                  <p className="text-blue-300 text-sm mt-1">{pkg.description}</p>
                                </div>
                              </div>
                              
                              
                            </div>
                            
                            <div className="ml-4 text-right">
                              <div className="text-white font-bold text-xl">${parseFloat(pkg.price.toString()).toFixed(2)}</div>
                            </div>
                          </div>
                        </div>
                      );
                    }).filter(Boolean)}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-3">📦</div>
                    <p className="text-blue-200">{t('checkout.noAvailablePackages')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mt-6">
            <div className="bg-red-500/20 border border-red-400 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <div className="text-red-400 text-xl">⚠️</div>
                <p className="text-red-200">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Receipt Modal */}
        {showReceiptModal && receiptData && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-auto">
              {/* Receipt Header */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-xl">
                <div className="text-center">
                  <div className="text-3xl mb-2">🎳</div>
                  <h2 className="text-xl font-bold">{t('checkout.receiptModal.title')}</h2>
                  <p className="text-blue-100 text-sm">{leagueName || 'Bowling League'}</p>
                </div>
              </div>

              {/* Receipt Body */}
              <div className="p-6 text-gray-800">
                {/* Player Info */}
                <div className="mb-6">
                  <h3 className="font-semibold text-lg mb-2">{t('checkout.playerInfo')}</h3>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
                    <p><span className="font-medium">{t('checkout.name')}:</span> {player?.bowlerName}</p>
                    <p><span className="font-medium">{t('checkout.id')}:</span> {player?.bowlerId}</p>
                    <p><span className="font-medium">{t('checkout.email')}:</span> {player?.email}</p>
                  </div>
                </div>

                {/* Package Details */}
                <div className="mb-6">
                  <h3 className="font-semibold text-lg mb-2">{t('checkout.receiptModal.packages')}</h3>
                  <div className="space-y-2">
                    {receiptData.packages.map((pkg, index) => (
                      <div key={index} className="flex justify-between items-center py-2 border-b border-gray-200">
                        <div>
                          <p className="font-medium">{pkg.name}</p>
                          <p className="text-sm text-gray-600">{pkg.description}</p>
                        </div>
                        <p className="font-semibold">${parseFloat(pkg.price.toString()).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Payment Summary */}
                <div className="mb-6">
                  <h3 className="font-semibold text-lg mb-2">{t('checkout.receiptModal.paymentSuccessful')}</h3>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between">
                      <span>{t('checkout.receiptModal.amount')}:</span>
                      <span className="font-semibold">${receiptData.amount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('checkout.receiptModal.method')}:</span>
                      <span className="font-semibold capitalize">{receiptData.paymentMethod}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('checkout.receiptModal.status')}:</span>
                      <span className={`font-semibold capitalize ${
                        receiptData.registrationStatus === 'completed' ? 'text-green-600' : 'text-blue-600'
                      }`}>
                        {receiptData.registrationStatus}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>{t('checkout.receiptModal.timestamp')}:</span>
                      <span>{receiptData.timestamp}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Receipt Actions */}
              <div className="flex space-x-3 p-6 bg-gray-50 rounded-b-xl">
                <button
                  onClick={() => window.print()}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition duration-200 flex items-center justify-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  <span>Print</span>
                </button>
                <button
                  onClick={() => {
                    setShowReceiptModal(false);
                    // Stay in the checkout dashboard, don't navigate away
                  }}
                  className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition duration-200 flex items-center justify-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span>{t('checkout.receiptModal.closeButton')}</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutComponent;